import type { ReactNode } from "react";
import {
	type CopyIconPosition,
	CopyToClipboard,
	type TooltipPlacement,
} from "@/components/base/copy-to-clipboard";
import { EmptyValue } from "@/components/common";
import { cx } from "@/utils/cx";

type DetailFieldVariant = "default" | "muted" | "emphasized";

export interface DetailFieldProps {
	label: string;
	value: ReactNode;
	editComponent?: ReactNode;
	isEditMode?: boolean;
	variant?: DetailFieldVariant;
	className?: string;
	valueClassName?: string;
	/**
	 * Whether to show a copy-to-clipboard button
	 * @default false
	 */
	copyable?: boolean;
	/**
	 * The text to copy to clipboard (if different from display value)
	 * If not provided, the string version of value will be used
	 */
	copyText?: string;
	/**
	 * Position of the copy icon
	 * @default "right"
	 */
	copyIconPosition?: CopyIconPosition;
	/**
	 * Tooltip placement for the copy button
	 * @default "top"
	 */
	copyTooltipPlacement?: TooltipPlacement;
}

const variantStyles: Record<DetailFieldVariant, string> = {
	default: "text-gray-700",
	muted: "text-gray-400",
	emphasized: "text-gray-900 font-semibold",
};

export function DetailField({
	label,
	value,
	editComponent,
	isEditMode = false,
	variant = "default",
	className,
	valueClassName,
	copyable = false,
	copyText,
	copyIconPosition = "right",
	copyTooltipPlacement = "top",
}: DetailFieldProps) {
	// Extract string value for copying
	const getCopyText = (): string => {
		if (copyText) return copyText;
		if (typeof value === "string") return value;
		if (typeof value === "number") return String(value);
		// For complex values, attempt to extract text content
		return String(value ?? "");
	};

	const displayValue = value ?? (
		<span className="text-gray-400">
			<EmptyValue />
		</span>
	);

	return (
		<div className={cx("space-y-1", className)}>
			{!isEditMode && (
				<label htmlFor={label} className="text-sm font-medium text-gray-700">
					{label}
				</label>
			)}
			{isEditMode && editComponent ? (
				<div>{editComponent}</div>
			) : copyable && value ? (
				<div>
					<CopyToClipboard
						text={getCopyText()}
						iconPosition={copyIconPosition}
						tooltipPlacement={copyTooltipPlacement}
						textClassName={cx(
							"text-md",
							variantStyles[variant],
							valueClassName,
						)}
					>
						{value}
					</CopyToClipboard>
				</div>
			) : (
				<div className={cx("text-md", variantStyles[variant], valueClassName)}>
					{displayValue}
				</div>
			)}
		</div>
	);
}

// Compound components for more complex layouts
interface DetailFieldGridProps {
	children: ReactNode;
	className?: string;
}

export function DetailFieldGrid({ children, className }: DetailFieldGridProps) {
	return (
		<div className={cx("flex flex-row justify-between gap-4", className)}>
			{children}
		</div>
	);
}

interface DetailFieldSectionProps {
	children: ReactNode;
	className?: string;
}

export function DetailFieldSection({
	children,
	className,
}: DetailFieldSectionProps) {
	return (
		<section className={cx("flex flex-col gap-4 flex-1", className)}>
			{children}
		</section>
	);
}

// Export as namespace for convenience
export const DetailFields = {
	Field: DetailField,
	Grid: DetailFieldGrid,
	Section: DetailFieldSection,
};
